import { createContext, useCallback, useContext, useMemo } from "react";

import { createQuestApi, type QuestApi } from "./api";
import { sortChartablePeriods } from "./periods";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { logger } from "@artis/logger";

const QuestContext = createContext<QuestApi | null>(null);

// If you want to use a local dev endpoint, set the "dev-authkeeper-endpoint" in localStorage
const LocalDevEndpoint = window.localStorage.getItem("dev-authkeeper-endpoint");

export const QuestApiProvider = ({ baseUrl, children }: { baseUrl: string; children: React.ReactNode }) => {
  const { getAccessTokenSilently } = useAuth0();
  const getToken = useCallback(
    () => getAccessTokenSilently({ authorizationParams: { audience: "hasura" } }),
    [getAccessTokenSilently],
  );
  const api = useMemo(
    () =>
      createQuestApi({
        baseUrl: LocalDevEndpoint ?? baseUrl,
        getToken,
      }),
    [baseUrl, getToken],
  );

  return <QuestContext.Provider value={api}>{children}</QuestContext.Provider>;
};

export const useQuestApi = () => {
  const api = useContext(QuestContext);
  if (!api) throw new Error("Missing QuestApiProvider");
  return api;
};

export const useChartablePeriods = (productId?: string) => {
  const api = useQuestApi();
  const months = useQuery({
    enabled: !!productId,
    queryKey: ["chartable-months", productId],
    staleTime: 1000,
    queryFn: async (p) => {
      if (!productId) return [];
      const periods = await api.periods(productId, p);
      const months = sortChartablePeriods(periods);

      // If a productId is set, then we expect to have periods available for it. So if none are returned, log an error for investigation.
      if (!periods?.length || !months?.length) {
        logger.error(`Quest API returned no periods for product ${productId}`, {
          periods,
          months,
        });
      }

      return months;
    },
  });
  return months;
};

export const useChartableProductIds = () => {
  const api = useQuestApi();
  return useQuery({
    queryKey: ["chartable-products"],
    queryFn: (p) => api.products(p),
  });
};
